import React, { useState, useEffect, useRef } from 'react';
import './StatusDropdown.scss';

// Shared Status Configuration
export const STATUS_CONFIG = {
    0: { label: 'TO DO', icon: 'fa-circle', iconType: 'dashed', color: '#ff9800', category: 'statuses' },
    1: { label: 'COMPLETE', icon: 'fa-check', color: '#34a853', category: 'closed' },
    2: { label: 'IN PROGRESS', icon: 'fa-clock', color: '#ffc53d', category: 'statuses' },
    3: { label: 'WAITING', icon: 'fa-clock', color: '#9c27b0', category: 'statuses' }
};

export const getStatusLabel = (status) => {
    return STATUS_CONFIG[status]?.label || 'TO DO';
};

export const getStatusIcon = (status) => {
    const config = STATUS_CONFIG[status];
    if (!config) return 'fa-circle';
    return config.icon;
};

export const getStatusColor = (status) => {
    return STATUS_CONFIG[status]?.color || '#ff9800';
};

const StatusDropdown = ({ 
    currentStatus = 0, 
    onStatusChange, 
    className = '',
    buttonClassName = '',
    size = 'medium', // 'small', 'medium', 'large'
    showLabel = false // Show label text next to icon
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDropdown && 
                dropdownRef.current && 
                !dropdownRef.current.contains(event.target) &&
                !event.target.closest(`.${className || 'status-dropdown-wrapper'}`)) {
                setShowDropdown(false);
                setSearchQuery('');
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, className]);

    const handleStatusSelect = (newStatus) => {
        if (onStatusChange) {
            onStatusChange(newStatus);
        }
        setShowDropdown(false);
        setSearchQuery('');
    };

    // Filter statuses based on search query
    const filteredStatuses = Object.entries(STATUS_CONFIG).filter(([status, config]) => {
        if (!searchQuery.trim()) return true;
        const searchLower = searchQuery.toLowerCase();
        return config.label.toLowerCase().includes(searchLower);
    });

    const statusConfig = STATUS_CONFIG[currentStatus ?? 0];
    const isComplete = currentStatus === 1;

    // Size classes
    const sizeClasses = {
        small: 'status-dropdown-btn-small',
        medium: 'status-dropdown-btn-medium',
        large: 'status-dropdown-btn-large'
    };

    return (
        <div className={`status-dropdown-wrapper ${className}`} ref={dropdownRef}>
            <button
                type="button"
                className={`status-dropdown-btn ${sizeClasses[size]} ${buttonClassName} ${showLabel ? 'status-dropdown-btn-with-label' : ''}`}
                onClick={() => setShowDropdown(!showDropdown)}
                style={showLabel ? {
                    backgroundColor: isComplete ? '#34a853' : undefined,
                    color: isComplete ? '#fff' : undefined,
                    borderColor: isComplete ? '#34a853' : undefined
                } : {
                    backgroundColor: isComplete ? '#34a853' : 'transparent',
                    borderColor: isComplete ? '#34a853' : getStatusColor(currentStatus ?? 0)
                }}
                title={getStatusLabel(currentStatus ?? 0)}
            >
                {statusConfig?.iconType === 'dashed' ? (
                    <span 
                        className="status-icon-dashed"
                        style={{ 
                            borderColor: isComplete ? '#fff' : getStatusColor(currentStatus ?? 0) 
                        }}
                    ></span>
                ) : (
                    <i 
                        className={`fa-solid ${getStatusIcon(currentStatus ?? 0)}`}
                        style={{ 
                            color: isComplete ? '#fff' : getStatusColor(currentStatus ?? 0),
                            fontSize: isComplete ? '12px' : '14px'
                        }}
                    ></i>
                )}
                {showLabel && (
                    <>
                        <span style={{ marginLeft: '8px' }}>{getStatusLabel(currentStatus ?? 0)}</span>
                        <i className="fa-solid fa-chevron-down" style={{ fontSize: '10px', marginLeft: '8px' }}></i>
                    </>
                )}
            </button>
            {showDropdown && (
                <div className="status-dropdown">
                    <input
                        type="text"
                        className="status-dropdown-search"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                    <div className="status-dropdown-list">
                        {/* Statuses Section */}
                        <div className="status-dropdown-section">
                            <div className="status-dropdown-section-header">Statuses</div>
                            {filteredStatuses
                                .filter(([status, config]) => config.category === 'statuses')
                                .map(([status, config]) => {
                                    const statusNum = parseInt(status, 10);
                                    const isSelected = (currentStatus ?? 0) === statusNum;
                                    return (
                                        <div
                                            key={status}
                                            className={`status-dropdown-item${isSelected ? ' selected' : ''}`}
                                            onClick={() => handleStatusSelect(statusNum)}
                                        >
                                            {config.iconType === 'dashed' ? (
                                                <span 
                                                    className="status-icon-dashed"
                                                    style={{ borderColor: config.color }}
                                                ></span>
                                            ) : (
                                                <i 
                                                    className={`fa-solid ${config.icon}`}
                                                    style={{ color: config.color }}
                                                ></i>
                                            )}
                                            <span>{config.label}</span>
                                            {isSelected && (
                                                <i className="fa-solid fa-check" style={{ marginLeft: 'auto' }}></i>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                        {/* Closed Section */}
                        <div className="status-dropdown-section">
                            <div className="status-dropdown-section-header">Closed</div>
                            {filteredStatuses
                                .filter(([status, config]) => config.category === 'closed')
                                .map(([status, config]) => {
                                    const statusNum = parseInt(status, 10);
                                    const isSelected = (currentStatus ?? 0) === statusNum;
                                    return (
                                        <div
                                            key={status}
                                            className={`status-dropdown-item${isSelected ? ' selected' : ''}`}
                                            onClick={() => handleStatusSelect(statusNum)}
                                        >
                                            <i 
                                                className={`fa-solid ${config.icon}`}
                                                style={{ color: config.color }}
                                            ></i>
                                            <span>{config.label}</span>
                                            {isSelected && (
                                                <i className="fa-solid fa-check" style={{ marginLeft: 'auto' }}></i>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatusDropdown;

